import React, { useState } from 'react'
import {
  CardsContainer,
  ExternalLink,
  LabraToolTipV2,
  OfferCard,
  StatusChipBg,
  StatusChipText,
  StatusChipV3,
  TableComponentV2,
  VerticalDivider,
} from '@labrav/react-components'
import {
  BuyerDetailRow,
  BuyerInfo,
} from '../../../common/components/DetailRow/DetailRow'

import { useStyles } from './Meterings.styles'

import { DateTime } from 'luxon'
import {
  standardLabraDateFormat,
  numberFormat,
  ShortenedNameV2,
} from '@labrav/common'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getCloudImg } from '../../../common/utils/getCloudImg'
import {
  faArrowRight,
  faChevronDown,
  faArrowTrendUp,
  faArrowTrendDown,
} from '@fortawesome/pro-regular-svg-icons'
import { useSelector } from '../../../store/hooks'

interface Status {
  statusText?: string
  statusColor: StatusChipBg
}

export interface DimensionWiseMetering {
  name: string
  description: string | null
  price: number
  quantity: number
  totalAmount: number
}

interface MeteringCardProps {
  offerName: string
  productName: string
  productId: string
  subscriptionId: string
  subscriptionStatus: string
  growthPercentage: number
  lastMeteringDate: string
  lastMeteringStatus: Status
  subscriptionStatusChip: Status
  lastMeteringAmount: number
  meteringSubmissions: number
  dimensionWiseMetering: DimensionWiseMetering[]
}

const MeteringCard: React.FC<MeteringCardProps> = ({
  offerName,
  productName,
  productId,
  subscriptionId,
  subscriptionStatus,
  growthPercentage,
  lastMeteringDate,
  lastMeteringStatus,
  subscriptionStatusChip,
  lastMeteringAmount,
  meteringSubmissions,
  dimensionWiseMetering,
}) => {
  const { classes } = useStyles()
  const timeZone = useSelector<string>(
    state => state.userProfile.userProfile?.timeZone || 'UTC'
  )
  const [showTable, setShowTable] = useState(false)

  const mapping = {
    Dimensions: 'name',
    Description: 'description',
    Price: 'price',
    Quantity: 'quantity',
    'Amount recieved': 'totalAmount',
  }

  const customObject = Object.values(dimensionWiseMetering).map(
    (dimension, index) => ({
      id: `${dimension.name}-${index}`,
      name: dimension.name,
      description: dimension.description,
      price: `$${dimension.price.toString()}`,
      quantity: dimension.quantity.toString(),
      totalAmount: `$${dimension.totalAmount.toString()}`,
    })
  )

  const getGrowthIcon = () => {
    if (growthPercentage > 0) {
      return faArrowTrendUp
    } else if (growthPercentage < 0) {
      return faArrowTrendDown
    } else {
      return faArrowRight
    }
  }

  const getGrowthColor = () => {
    if (growthPercentage > 0) {
      return 'green'
    } else if (growthPercentage < 0) {
      return 'red'
    } else {
      return 'orange'
    }
  }

  const getGrowthLabel = () => {
    if (growthPercentage > 0) {
      return `${growthPercentage}% increase in usage`
    } else if (growthPercentage < 0) {
      return `${Math.abs(growthPercentage)}% decrease in usage`
    }
    return 'Constant usage'
  }

  return (
    <CardsContainer>
      <OfferCard
        name={
          <div className={classes.name}>
            <ShortenedNameV2
              name={offerName !== '' ? offerName : productName}
              length={40}
            ></ShortenedNameV2>
            <LabraToolTipV2
              tooltipPlacement="top"
              label="Labra subscription ID"
            >
              <div className={classes.account}>{subscriptionId}</div>
            </LabraToolTipV2>
          </div>
        }
        isLastCard={true}
        customiseForBuyer={true}
        hideLogo={true}
        rightSideSection={
          <>
            {subscriptionStatus !== 'UNSUBSCRIBE_SUCCESS' && (
              <ExternalLink
                href={`/flyout/products-listing/${productId}/contracts/${subscriptionId}`}
              >
                Create metering
              </ExternalLink>
            )}
            <VerticalDivider height="24px"></VerticalDivider>
            <StatusChipV3
              bgColor={subscriptionStatusChip.statusColor}
              textColor={StatusChipText.GRAY}
              label={subscriptionStatusChip.statusText || ''}
            ></StatusChipV3>
            <LabraToolTipV2 tooltipPlacement="top" label={getGrowthLabel()}>
              <FontAwesomeIcon
                icon={getGrowthIcon()}
                className={classes.copyIcon}
                color={getGrowthColor()}
              />
            </LabraToolTipV2>
            <img
              src={getCloudImg('AWS')}
              data-testid={`icon1-${1}`}
              style={{ width: '24px', height: '24px' }}
            />
          </>
        }
      />

      <BuyerDetailRow
        children={
          <>
            <BuyerInfo
              title={'Last metering date'}
              value={
                <div>
                  {lastMeteringDate
                    ? DateTime.fromISO(lastMeteringDate)
                        .setZone(timeZone)
                        .toFormat(standardLabraDateFormat)
                    : '-'}
                </div>
              }
              gridSize={2}
            />
            <BuyerInfo
              title={'No. of times metered'}
              value={
                <div>{meteringSubmissions ? meteringSubmissions : '-'}</div>
              }
              gridSize={2}
            />
            <BuyerInfo
              title={'Last metering status'}
              value={
                <StatusChipV3
                  bgColor={lastMeteringStatus.statusColor || StatusChipBg.GREEN}
                  textColor={StatusChipText.GRAY}
                  label={lastMeteringStatus.statusText || ''}
                />
              }
              gridSize={4}
            />
            <BuyerInfo
              title={'Last metering amount'}
              value={
                <>
                  <span className={classes.currency}>$</span>
                  {String(numberFormat(lastMeteringAmount))}
                </>
              }
              gridSize={2}
            />
            <BuyerInfo
              title={'Show dimension wise metering'}
              value={
                <FontAwesomeIcon
                  icon={faChevronDown}
                  onClick={() => setShowTable(!showTable)}
                  border={true}
                  className={classes.copyIcon}
                />
              }
              gridSize={2}
              showDivider={false}
            />
          </>
        }
      />
      {showTable && (
        <div className={classes.tableWrapper}>
          <TableComponentV2
            mapping={mapping}
            objects={customObject}
            count={20}
            keyName="dimensional-breakdown-metering-data."
            showCheckbox={false}
            hasMoreData={false}
            fetchMoreData={() => {}}
            selectedItem={[]}
            onChangeCheckbox={() => {}}
            isLoading={false}
            isBasicTable={true}
          />
        </div>
      )}
    </CardsContainer>
  )
}

export default MeteringCard
